import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  ProjectAccessActionTypes,
  ProjectAccessListLoad,
  ProjectAccessListLoadSuccess,
  ProjectAccessLoad,
  ProjectAccessLoadSuccess,
  ProjectAccessMasksLoad,
  ProjectAccessMasksLoadSuccess,
  ProjectAccessMasksUpdateSuccess,
  ProjectSignatureMasksLoad,
  ProjectSignatureMasksLoadSuccess,
  ProjectSignatureMasksUpdateSuccess,
  UpdateProjectAccess,
  UpdateProjectAccessMask,
  UpdateProjectSignatureMask,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { ProjectAccessService } from '../../services';
import { Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class ProjectAccessEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly projectAccessService: ProjectAccessService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  loadProjectAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectAccessLoad>(ProjectAccessActionTypes.ProjectAccessLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'getProjectAccess',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new ProjectAccessLoadSuccess(response as any),
      }),
    ),
  );

  loadProjectAccessList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectAccessListLoad>(ProjectAccessActionTypes.ProjectAccessListLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'getProjectAccessList',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new ProjectAccessListLoadSuccess(response as any),
      }),
    ),
  );

  // Load all assignment masks for the current project
  loadProjectAccessMasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectAccessMasksLoad>(ProjectAccessActionTypes.ProjectAccessMasksLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'getProjectAccessMasks',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new ProjectAccessMasksLoadSuccess(response as any),
      }),
    ),
  );

  // Load all signature masks for the current project
  loadProjectSignatureMasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectSignatureMasksLoad>(ProjectAccessActionTypes.ProjectSignatureMasksLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'getProjectSignatureMasks',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new ProjectSignatureMasksLoadSuccess(response as any),
      }),
    ),
  );

  updateProjectAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateProjectAccess>(ProjectAccessActionTypes.UpdateProjectAccess),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'updateProjectAccess',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.projectAccess],
        onSuccess: (response) => new ProjectAccessListLoadSuccess(response as any),
      }),
    ),
  );

  // Update a single assignment mask
  updateProjectAccessMask$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateProjectAccessMask>(ProjectAccessActionTypes.UpdateProjectAccessMask),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'updateProjectAccessMask',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.projectAccessId, action.mask],
        // Assume backend returns the updated ProjectAccessAssignmentMask entry
        onSuccess: (response) => new ProjectAccessMasksUpdateSuccess(response as any),
      }),
    ),
  );

  // Update a single signature mask
  updateProjectSignatureMask$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateProjectSignatureMask>(ProjectAccessActionTypes.UpdateProjectSignatureMask),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.projectAccessService,
        method: 'updateProjectSignatureMask',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.projectAccessId, action.mask],
        onSuccess: (response) => new ProjectSignatureMasksUpdateSuccess(response as any),
      }),
    ),
  );
}
